/**
 * @fileoverview Componente responsável por criar e editar conexões do WhatsApp.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Typography,
  Box,
  Chip,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import {
  Wifi,
  Cloud,
  Lock,
  Copy,
  Check,
  Clock,
  Pencil,
  Plus,
  X,
  MessageSquare,
  ExternalLink,
} from "lucide-react";

import api, { API_BASE_URL } from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useQueues } from "../../context/Queues/QueuesContext";
import MessageVariablesPicker from "../MessageVariablesPicker";
import SchedulesForm from "../SchedulesForm";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '24px',
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
        : '#FFFFFF',
      // backdrop-filter aqui estava forçando o navegador a recompor TODA a
      // área da página atrás do modal a cada frame (blur 40px + saturate). Com
      // o fundo já 95% opaco, o efeito era praticamente invisível — só custo.
      // Resultado: CPU 100% sustentado ao abrir o modal na página Connections,
      // que também tem múltiplos blurs e uma animação pulseDot. Removido.
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : 'none',
      boxShadow: theme.palette.type === 'dark'
        ? '0 24px 48px rgba(0, 0, 0, 0.6)'
        : '0 24px 48px rgba(0, 0, 0, 0.12)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: theme.palette.type === 'dark'
          ? 'linear-gradient(90deg, #5B7CFF 0%, #9D7CFF 100%)'
          : 'linear-gradient(90deg, #3D5CFD 0%, #7C3AED 100%)',
        borderRadius: '24px 24px 0 0',
      },
    },
    '& .MuiBackdrop-root': {
      // backdrop-filter custa GPU/CPU pesado porque o navegador precisa
      // recompor toda a área atrás do overlay a cada frame. Combinado com
      // o pulseDot e os blurs das chips na página Connections por trás,
      // prendia CPU em 100%. O escuro sólido (0.7 alpha) basta visualmente.
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)',
    },
  },

  dialogTitle: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    '& .MuiTypography-root': {
      fontSize: '1.5rem',
      fontWeight: 700,
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, #FFFFFF 0%, rgba(255, 255, 255, 0.8) 100%)'
        : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
  },

  tabsContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: `0 ${theme.spacing(3)}px`,
    paddingBottom: theme.spacing(2),
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.1)'
      : '1px solid rgba(0, 0, 0, 0.06)',
  },

  tab: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: '12px 20px',
    borderRadius: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fontSize: '0.9rem',
    fontWeight: 600,
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.06)'
      : '1px solid rgba(0, 0, 0, 0.06)',
    background: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)',
    color: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.5)'
      : '#94a3b8',
  },

  tabActive: {
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(91, 124, 255, 0.15) 0%, rgba(157, 124, 255, 0.15) 100%)'
      : 'linear-gradient(135deg, rgba(61, 92, 253, 0.08) 0%, rgba(124, 58, 237, 0.08) 100%)',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.3) !important'
      : '1px solid rgba(61, 92, 253, 0.2) !important',
    color: theme.palette.type === 'dark'
      ? '#93c5fd'
      : '#3D5CFD',
    boxShadow: theme.palette.type === 'dark'
      ? '0 4px 16px rgba(91, 124, 255, 0.15)'
      : '0 4px 16px rgba(61, 92, 253, 0.1)',
  },

  tabDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    '&:hover': {
      background: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)',
      border: theme.palette.type === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.06)'
        : '1px solid rgba(0, 0, 0, 0.06)',
    },
  },

  dialogContent: {
    padding: theme.spacing(3),
    maxHeight: '60vh',
    overflowY: 'auto',
    ...theme.scrollbarStyles,
    '&.MuiDialogContent-dividers': {
      borderTop: 'none',
      borderBottom: 'none',
    },
  },

  sectionTitle: {
    fontSize: '0.8rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    '&:first-child': {
      marginTop: 0,
    },
  },

  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.12)',
      },
      '&.Mui-focused': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.02)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.5)' : '1px solid #3D5CFD',
        boxShadow: theme.palette.type === 'dark'
          ? '0 0 0 1px rgba(91, 124, 255, 0.15)'
          : '0 0 0 1px rgba(61, 92, 253, 0.1)',
      },
      '& fieldset': {
        border: 'none',
      },
    },
    '& .MuiInputLabel-outlined': {
      color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
      fontWeight: 500,
      '&.Mui-focused': {
        color: theme.palette.type === 'dark' ? '#93c5fd' : '#3D5CFD',
      },
    },
  },

  readOnlyField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
      border: theme.palette.type === 'dark' ? '1px dashed rgba(91, 124, 255, 0.15)' : '1px dashed rgba(0, 0, 0, 0.12)',
      '& fieldset': {
        border: 'none',
      },
      '& input': {
        color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#94a3b8',
      },
    },
    '& .MuiInputLabel-outlined': {
      color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
      fontWeight: 500,
    },
  },

  queueChipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: '14px',
    background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)',
    minHeight: 48,
    alignItems: 'center',
  },

  queueChip: {
    borderRadius: '10px',
    fontWeight: 600,
    fontSize: '0.8rem',
    height: 32,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'scale(1.03)',
    },
  },

  selectAllChip: {
    borderRadius: '10px',
    fontWeight: 600,
    fontSize: '0.8rem',
    height: 32,
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(91, 124, 255, 0.2) 0%, rgba(157, 124, 255, 0.2) 100%)'
      : 'linear-gradient(135deg, rgba(61, 92, 253, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
    color: theme.palette.type === 'dark' ? '#93c5fd' : '#3D5CFD',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.3)'
      : '1px solid rgba(61, 92, 253, 0.2)',
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, rgba(91, 124, 255, 0.3) 0%, rgba(157, 124, 255, 0.3) 100%)'
        : 'linear-gradient(135deg, rgba(61, 92, 253, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)',
    },
  },

  infoBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: '10px 14px',
    borderRadius: '12px',
    background: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.04)',
    border: theme.palette.type === 'dark' ? '1px solid rgba(59, 130, 246, 0.15)' : '1px solid rgba(59, 130, 246, 0.08)',
    fontSize: '0.8rem',
    color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    fontWeight: 500,
    marginBottom: theme.spacing(2),
  },

  copyButton: {
    padding: 4,
    borderRadius: '8px',
    minWidth: 'auto',
    color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    '&:hover': {
      background: theme.palette.type === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.08)',
    },
  },

  messageField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      fontSize: '0.875rem',
      '&:hover': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.03)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
      },
      '&.Mui-focused': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.01)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.4)' : '1px solid #3D5CFD',
        boxShadow: theme.palette.type === 'dark'
          ? '0 0 0 1px rgba(91, 124, 255, 0.1)'
          : '0 0 0 1px rgba(61, 92, 253, 0.08)',
      },
      '& fieldset': {
        border: 'none',
      },
    },
    '& .MuiInputLabel-outlined': {
      color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#94a3b8',
      fontWeight: 500,
      '&.Mui-focused': {
        color: theme.palette.type === 'dark' ? '#93c5fd' : '#3D5CFD',
      },
    },
  },

  formControl: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.12)',
      },
      '&.Mui-focused': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.02)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.5)' : '1px solid #3D5CFD',
        boxShadow: theme.palette.type === 'dark'
          ? '0 0 0 1px rgba(91, 124, 255, 0.15)'
          : '0 0 0 1px rgba(61, 92, 253, 0.1)',
      },
      '& fieldset': {
        border: 'none',
      },
    },
    '& .MuiInputLabel-outlined': {
      color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
      fontWeight: 500,
      '&.Mui-focused': {
        color: theme.palette.type === 'dark' ? '#93c5fd' : '#3D5CFD',
      },
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  switchLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
    },
  },

  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.1)'
      : '1px solid rgba(0, 0, 0, 0.06)',
  },

  cancelBtn: {
    minWidth: 120,
    height: 40,
    padding: '0 24px',
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    borderColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
    '&:hover': {
      borderColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.2)',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
    },
  },

  submitBtn: {
    minWidth: 120,
    height: 40,
    padding: '0 24px',
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    color: '#ffffff',
    boxShadow: 'none',
    background: 'linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)',
    transition: 'all 180ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      background: 'linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
      color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)',
    },
  },

  expiresRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },

  // Header da seção com switch alinhado à direita do título — reaproveita
  // sectionTitle mas neutraliza margin para caber no flex row.
  autoCloseHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
    '&:first-child': {
      marginTop: 0,
    },
  },
  autoCloseHeaderTitle: {
    fontSize: '0.8rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8',
  },
  expiresUnit: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#64748b',
    whiteSpace: 'nowrap',
  },
}));

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("whatsappModal.formErrors.name.short"))
    .max(50, i18n.t("whatsappModal.formErrors.name.long"))
    .required(i18n.t("whatsappModal.formErrors.name.required")),
  provider: Yup.string().required("Tipo de conexão obrigatório"),
  metaAccessToken: Yup.string().nullable().when("provider", {
    is: "meta-api",
    then: Yup.string().required("Access Token obrigatório"),
    otherwise: Yup.string().nullable()
  }),
  metaPhoneNumberId: Yup.string().nullable().when("provider", {
    is: "meta-api",
    then: Yup.string().required("Phone Number ID obrigatório"),
    otherwise: Yup.string().nullable()
  }),
  metaBusinessAccountId: Yup.string().nullable().when("provider", {
    is: "meta-api",
    then: Yup.string().required("Business Account ID obrigatório"),
    otherwise: Yup.string().nullable()
  }),
  metaAppId: Yup.string().nullable().when("provider", {
    is: "meta-api",
    then: Yup.string().required("App ID obrigatório"),
    otherwise: Yup.string().nullable()
  }),
  metaWebhookToken: Yup.string().nullable(),
  metaApiVersion: Yup.string().nullable(),
  expiresTicket: Yup.number()
    .typeError(i18n.t("whatsappModal.formErrors.expiresTicket.number"))
    .integer(i18n.t("whatsappModal.formErrors.expiresTicket.integer"))
    .min(0, i18n.t("whatsappModal.formErrors.expiresTicket.min"))
    .max(43200, i18n.t("whatsappModal.formErrors.expiresTicket.max")),
  expiresInactiveMessage: Yup.string()
    .max(1000, i18n.t("whatsappModal.formErrors.expiresInactiveMessage.max")),
  greetingAcceptedMessage: Yup.string()
    .max(1000, i18n.t("whatsappModal.formErrors.greetingAcceptedMessage.max")),
  livechatTitle: Yup.string().nullable().when("provider", {
    is: "livechat",
    then: Yup.string()
      .required(i18n.t("whatsappModal.formErrors.livechatTitle.required"))
      .max(40, i18n.t("whatsappModal.formErrors.livechatTitle.max")),
    otherwise: Yup.string().nullable(),
  }),
  livechatSubtitle: Yup.string().nullable().max(80),
  livechatButtonText: Yup.string().nullable().max(30),
  livechatWelcomeMessage: Yup.string().nullable().max(1000),
  livechatColor: Yup.string().nullable().when("provider", {
    is: "livechat",
    then: Yup.string().matches(
      /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
      i18n.t("whatsappModal.formErrors.livechatColor.invalid")
    ),
    otherwise: Yup.string().nullable(),
  }),
  livechatDefaultCountryCode: Yup.string().nullable().when("provider", {
    is: "livechat",
    then: Yup.string().matches(
      /^\+?\d{1,3}$/,
      i18n.t("whatsappModal.formErrors.livechatDefaultCountryCode.invalid")
    ),
    otherwise: Yup.string().nullable(),
  }),
  livechatAllowedDomains: Yup.string().nullable().when("provider", {
    is: "livechat",
    then: Yup.string()
      .required(i18n.t("whatsappModal.formErrors.livechatAllowedDomains.required"))
      .test(
        "has-domain",
        i18n.t("whatsappModal.formErrors.livechatAllowedDomains.required"),
        (value) => !!value && value.split(/[\n,]/).some((d) => d.trim().length > 0)
      ),
    otherwise: Yup.string().nullable(),
  }),
});

// livechatConfig (JSONB no backend) ↔ campos flat do formulário
const flattenLivechatConfig = (config) => {
  const c = config || {};
  return {
    livechatTitle: c.title || "",
    livechatSubtitle: c.subtitle || "",
    livechatWelcomeMessage: c.welcomeMessage || "",
    livechatColor: c.primaryColor || "#6366f1",
    livechatButtonText: c.buttonText || "",
    livechatPhoneRequired: c.phoneRequired === true,
    livechatDefaultCountryCode: c.defaultDdi ? `+${c.defaultDdi}` : "+55",
    livechatDefaultQueueId: c.defaultQueueId || "",
    livechatAllowedDomains: Array.isArray(c.allowedDomains)
      ? c.allowedDomains.join("\n")
      : "",
    livechatEnabled: c.enabled !== false,
  };
};

const buildLivechatConfig = (values) => ({
  enabled: values.livechatEnabled !== false,
  title: values.livechatTitle,
  subtitle: values.livechatSubtitle,
  welcomeMessage: values.livechatWelcomeMessage,
  primaryColor: values.livechatColor,
  buttonText: values.livechatButtonText,
  phoneRequired: values.livechatPhoneRequired === true,
  defaultDdi: String(values.livechatDefaultCountryCode || "+55").replace(/^\+/, ""),
  defaultQueueId: values.livechatDefaultQueueId || null,
  allowedDomains: String(values.livechatAllowedDomains || "")
    .split(/[\n,]/)
    .map((d) => d.trim())
    .filter(Boolean),
});

const buildLivechatSnippet = (widgetId) =>
  `<script src="${API_BASE_URL}/livechat/widget.js" data-connection-id="${widgetId}" async></script>`;

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { queues } = useQueues();

  const initialState = {
    name: "",
    greetingMessage: "",
    complationMessage: "",
    outOfHoursMessage: "",
    ratingMessage: "",
    isDefault: false,
    token: "",
    provider: "waha",
    expiresInactiveMessage: "",
    expiresTicket: 0,
    greetingAcceptedMessage: "",
    greetingAcceptedEnabled: false,
    sendTransferQueueMessageEnabled: false,
    sendTransferUserMessageEnabled: false,
    sendTransferQueueAndUserMessageEnabled: false,
    timeUseBotQueues: 0,
    maxUseBotQueues: 3,
    integration: null,
    metaAccessToken: "",
    metaPhoneNumberId: "",
    metaBusinessAccountId: "",
    metaAppId: "",
    metaAppSecret: "",
    metaWebhookToken: "sttelar",
    metaApiVersion: "v24.0",
    ...flattenLivechatConfig(null),
  };

  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [copiedField, setCopiedField] = useState(null);
  const [schedulesOverride, setSchedulesOverride] = useState(false);
  const [whatsappSchedules, setWhatsappSchedules] = useState(null);
  const [schedulesSaving, setSchedulesSaving] = useState(false);
  // Stash do último `expiresTicket` ativo pra restaurar quando o usuário
  // religar o switch sem precisar redigitar o valor.
  const lastExpiresTicketRef = useRef(30);
  // Ref do <textarea> da mensagem de encerramento — usado pelo
  // MessageVariablesPicker pra inserir a variável na posição do cursor.
  const expiresMessageRef = useRef(null);
  // Mesmo padrão pra mensagem de saudação no aceite.
  const greetingAcceptedRef = useRef(null);

  // Refs sincronizadas com o estado do Formik dentro do render prop.
  // Servem pra que os handlers do MessageVariablesPicker sejam estáveis
  // (useCallback deps vazias) e ainda leiam o valor atual no momento do
  // clique — sem isso, React.memo no picker perderia efeito porque o
  // onClick seria novo a cada keystroke.
  const formikValuesRef = useRef(null);
  const formikSetFieldValueRef = useRef(null);

  const makeVariablePicker = useCallback((fieldName, inputRef) => (variable) => {
    const setField = formikSetFieldValueRef.current;
    if (!setField) return;
    const el = inputRef.current;
    const current = formikValuesRef.current?.[fieldName] || "";
    if (el && typeof el.selectionStart === "number" && document.activeElement === el) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = current.slice(0, start) + variable + current.slice(end);
      setField(fieldName, next);
      setTimeout(() => {
        try {
          const pos = start + variable.length;
          el.focus();
          el.setSelectionRange(pos, pos);
        } catch { /* ignora */ }
      }, 0);
    } else {
      setField(fieldName, current + variable);
    }
  }, []);

  const pickExpiresInactiveVar = useMemo(
    () => makeVariablePicker("expiresInactiveMessage", expiresMessageRef),
    [makeVariablePicker]
  );
  const pickGreetingAcceptedVar = useMemo(
    () => makeVariablePicker("greetingAcceptedMessage", greetingAcceptedRef),
    [makeVariablePicker]
  );

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;
      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}?session=0`);
        setWhatsApp({
          ...initialState,
          ...data,
          metaWebhookToken: data.metaWebhookToken || "sttelar",
          metaApiVersion: data.metaApiVersion || "v24.0",
          ...(data.provider === "livechat"
            ? flattenLivechatConfig(data.livechatConfig)
            : {}),
        });
        if (data.expiresTicket && Number(data.expiresTicket) > 0) {
          lastExpiresTicketRef.current = Number(data.expiresTicket);
        }
        const whatsQueueIds = data.queues?.map((queue) => queue.id) || [];
        setSelectedQueueIds(whatsQueueIds);

        if (Array.isArray(data.schedules) && data.schedules.length > 0) {
          // Os horários antes de ligar o "personalizado": o formulário já monta
          // com a semana (sem piscar "nenhum horário").
          setWhatsappSchedules(data.schedules);
          setSchedulesOverride(true);
        } else {
          setSchedulesOverride(false);
          setWhatsappSchedules(null);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  const handleSaveSchedules = async (schedulesPayload) => {
    if (!whatsAppId) {
      toast.info(i18n.t("whatsappModal.schedules.saveFirst"));
      return;
    }
    setSchedulesSaving(true);
    try {
      await api.put(`/whatsapp/${whatsAppId}/schedules`, {
        schedules: schedulesOverride ? schedulesPayload : null,
      });
      toast.success(i18n.t("whatsappModal.schedules.saved"));
      if (schedulesOverride) setWhatsappSchedules(schedulesPayload);
    } catch (err) {
      toastError(err);
    } finally {
      setSchedulesSaving(false);
    }
  };

  const handleToggleSchedulesOverride = async (event) => {
    const next = event.target.checked;
    setSchedulesOverride(next);
    if (!next && whatsAppId) {
      setSchedulesSaving(true);
      try {
        await api.put(`/whatsapp/${whatsAppId}/schedules`, { schedules: null });
        setWhatsappSchedules(null);
        toast.success(i18n.t("whatsappModal.schedules.restored"));
      } catch (err) {
        toastError(err);
        setSchedulesOverride(true);
      } finally {
        setSchedulesSaving(false);
      }
    }
  };

  const handleSaveWhatsApp = async (values) => {
    const isLivechat = values.provider === "livechat";
    // whatsApp.id cobre o "modo edição virtual" pós-criação do livechat
    const effectiveId = whatsAppId || whatsApp.id;

    const whatsappData = {
      ...values,
      queueIds: isLivechat
        ? (values.livechatDefaultQueueId ? [values.livechatDefaultQueueId] : [])
        : selectedQueueIds,
      metaWebhookToken: "sttelar",
      metaApiVersion: "v24.0",
      ...(isLivechat ? { livechatConfig: buildLivechatConfig(values) } : {}),
    };
    delete whatsappData["queues"];
    delete whatsappData["session"];
    // campos flat do livechat não existem no backend — vão via livechatConfig
    Object.keys(whatsappData)
      .filter((k) => k.startsWith("livechat") && k !== "livechatConfig")
      .forEach((k) => delete whatsappData[k]);

    try {
      if (effectiveId) {
        await api.put(`/whatsapp/${effectiveId}`, whatsappData);
        toast.success(i18n.t("whatsappModal.success"));
        handleClose();
      } else {
        const { data } = await api.post("/whatsapp", whatsappData);
        if (isLivechat && data?.livechatWidgetId) {
          // Não fecha: re-renderiza em "modo edição virtual" exibindo o
          // código de instalação recém-gerado (livechatWidgetId).
          setWhatsApp({
            ...initialState,
            ...data,
            ...flattenLivechatConfig(data.livechatConfig),
          });
          toast.success(i18n.t("whatsappModal.livechat.createdCopyCode"));
        } else {
          toast.success(i18n.t("whatsappModal.success"));
          handleClose();
        }
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
    setSelectedQueueIds([]);
  };

  const handleToggleQueue = (queueId) => {
    setSelectedQueueIds((prev) =>
      prev.includes(queueId)
        ? prev.filter((id) => id !== queueId)
        : [...prev, queueId]
    );
  };

  const handleSelectAllQueues = () => {
    if (selectedQueueIds.length === queues.length) {
      setSelectedQueueIds([]);
    } else {
      setSelectedQueueIds(queues.map((q) => q.id));
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleTabChange = (provider, setFieldValue) => {
    // whatsApp.id cobre o "modo edição virtual" logo após criar livechat
    if (whatsAppId || whatsApp.id) return;
    setFieldValue("provider", provider);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        className={classes.dialog}
      >
        <DialogTitle className={classes.dialogTitle}>
          {whatsAppId
            ? i18n.t("whatsappModal.title.edit")
            : i18n.t("whatsappModal.title.add")}
        </DialogTitle>

        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          validateOnChange={false}
          validateOnBlur={true}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => {
            formikValuesRef.current = values;
            formikSetFieldValueRef.current = setFieldValue;
            return (
            <Form>
              {/* TABS */}
              <div className={classes.tabsContainer}>
                <div
                  className={`${classes.tab} ${values.provider === "waha" ? classes.tabActive : ""} ${whatsAppId && values.provider !== "waha" ? classes.tabDisabled : ""}`}
                  onClick={() => handleTabChange("waha", setFieldValue)}
                >
                  <Wifi size={18} strokeWidth={2} />
                  WhatsApp Web
                </div>
                <div
                  className={`${classes.tab} ${values.provider === "meta-api" ? classes.tabActive : ""} ${whatsAppId && values.provider !== "meta-api" ? classes.tabDisabled : ""}`}
                  onClick={() => handleTabChange("meta-api", setFieldValue)}
                >
                  <Cloud size={18} strokeWidth={2} />
                  API Oficial
                </div>
                <div
                  className={`${classes.tab} ${values.provider === "livechat" ? classes.tabActive : ""} ${(whatsAppId || whatsApp.id) && values.provider !== "livechat" ? classes.tabDisabled : ""}`}
                  onClick={() => handleTabChange("livechat", setFieldValue)}
                >
                  <MessageSquare size={18} strokeWidth={2} />
                  LiveChat
                </div>
              </div>

              <DialogContent className={classes.dialogContent} dividers>
                {/* NOME + PADRAO */}
                <Typography className={classes.sectionTitle}>
                  Identificação
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs>
                    <Field
                      as={TextField}
                      label={i18n.t("whatsappModal.form.name")}
                      autoFocus
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.textField}
                    />
                  </Grid>
                  {values.provider !== "livechat" && (
                    <Grid item>
                      <FormControlLabel
                        className={classes.switchLabel}
                        control={
                          <Field
                            as={Switch}
                            color="primary"
                            name="isDefault"
                            checked={values.isDefault}
                          />
                        }
                        label={i18n.t("whatsappModal.form.default")}
                      />
                    </Grid>
                  )}
                </Grid>

                {/* META API FIELDS */}
                {values.provider === "meta-api" && (
                  <>
                    <Typography className={classes.sectionTitle}>
                      Credenciais da API
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.form.metaAccessToken")}
                          fullWidth
                          name="metaAccessToken"
                          variant="outlined"
                          margin="dense"
                          error={touched.metaAccessToken && Boolean(errors.metaAccessToken)}
                          helperText={touched.metaAccessToken && errors.metaAccessToken}
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.form.metaPhoneNumberId")}
                          fullWidth
                          name="metaPhoneNumberId"
                          variant="outlined"
                          margin="dense"
                          error={touched.metaPhoneNumberId && Boolean(errors.metaPhoneNumberId)}
                          helperText={touched.metaPhoneNumberId && errors.metaPhoneNumberId}
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.form.metaBusinessAccountId")}
                          fullWidth
                          name="metaBusinessAccountId"
                          variant="outlined"
                          margin="dense"
                          error={touched.metaBusinessAccountId && Boolean(errors.metaBusinessAccountId)}
                          helperText={touched.metaBusinessAccountId && errors.metaBusinessAccountId}
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.form.metaAppId")}
                          fullWidth
                          name="metaAppId"
                          variant="outlined"
                          margin="dense"
                          error={touched.metaAppId && Boolean(errors.metaAppId)}
                          helperText={touched.metaAppId && errors.metaAppId}
                          className={classes.textField}
                        />
                      </Grid>
                      {/* App Secret: valida a assinatura dos webhooks da Meta.
                          Cada cliente tem o proprio App, entao o secret e por
                          conexao. Em branco = webhook aceito sem verificacao. */}
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.form.metaAppSecret")}
                          fullWidth
                          type="password"
                          name="metaAppSecret"
                          variant="outlined"
                          margin="dense"
                          error={touched.metaAppSecret && Boolean(errors.metaAppSecret)}
                          helperText={
                            (touched.metaAppSecret && errors.metaAppSecret) ||
                            i18n.t("whatsappModal.form.metaAppSecretHelp")
                          }
                          className={classes.textField}
                        />
                      </Grid>
                    </Grid>

                    {/* READ-ONLY INFO FIELDS */}
                    <Typography className={classes.sectionTitle}>
                      Configurações fixas
                    </Typography>
                    <div className={classes.infoBox}>
                      <Lock size={14} strokeWidth={2} />
                      Esses valores são fixos e não podem ser alterados
                    </div>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Webhook Verify Token"
                          fullWidth
                          value="sttelar"
                          variant="outlined"
                          margin="dense"
                          className={classes.readOnlyField}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <Button
                                className={classes.copyButton}
                                size="small"
                                onClick={() => handleCopy("sttelar", "webhook")}
                              >
                                {copiedField === "webhook" ? <Check size={14} /> : <Copy size={14} />}
                              </Button>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Graph API Version"
                          fullWidth
                          value="v24.0"
                          variant="outlined"
                          margin="dense"
                          className={classes.readOnlyField}
                          InputProps={{
                            readOnly: true,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {/* LIVECHAT FIELDS */}
                {values.provider === "livechat" && (
                  <>
                    <Typography className={classes.sectionTitle}>
                      {i18n.t("whatsappModal.livechat.sectionAppearance")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.form.livechatTitle")}
                          fullWidth
                          name="livechatTitle"
                          variant="outlined"
                          margin="dense"
                          error={touched.livechatTitle && Boolean(errors.livechatTitle)}
                          helperText={touched.livechatTitle && errors.livechatTitle}
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.form.livechatSubtitle")}
                          fullWidth
                          name="livechatSubtitle"
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.form.livechatButtonText")}
                          fullWidth
                          name="livechatButtonText"
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.form.livechatColor")}
                          fullWidth
                          name="livechatColor"
                          variant="outlined"
                          margin="dense"
                          error={touched.livechatColor && Boolean(errors.livechatColor)}
                          helperText={touched.livechatColor && errors.livechatColor}
                          className={classes.textField}
                          InputProps={{
                            endAdornment: (
                              <input
                                type="color"
                                value={/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(values.livechatColor || "") ? values.livechatColor : "#6366f1"}
                                onChange={(e) => setFieldValue("livechatColor", e.target.value)}
                                style={{ width: 28, height: 28, border: "none", background: "transparent", cursor: "pointer", padding: 0 }}
                                aria-label={i18n.t("whatsappModal.form.livechatColor")}
                              />
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Typography className={classes.sectionTitle}>
                      {i18n.t("whatsappModal.livechat.sectionBehavior")}
                    </Typography>
                    <Field
                      as={TextField}
                      label={i18n.t("whatsappModal.form.livechatWelcomeMessage")}
                      multiline
                      rows={3}
                      fullWidth
                      name="livechatWelcomeMessage"
                      variant="outlined"
                      margin="dense"
                      className={classes.messageField}
                    />
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <Field
                          as={TextField}
                          label={i18n.t("whatsappModal.form.livechatDefaultCountryCode")}
                          fullWidth
                          name="livechatDefaultCountryCode"
                          variant="outlined"
                          margin="dense"
                          error={touched.livechatDefaultCountryCode && Boolean(errors.livechatDefaultCountryCode)}
                          helperText={
                            (touched.livechatDefaultCountryCode && errors.livechatDefaultCountryCode) ||
                            i18n.t("whatsappModal.form.livechatDefaultCountryCodeHelp")
                          }
                          className={classes.textField}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl variant="outlined" margin="dense" fullWidth className={classes.textField}>
                          <InputLabel id="livechat-queue-label">
                            {i18n.t("whatsappModal.form.livechatDefaultQueue")}
                          </InputLabel>
                          <Select
                            labelId="livechat-queue-label"
                            label={i18n.t("whatsappModal.form.livechatDefaultQueue")}
                            value={values.livechatDefaultQueueId || ""}
                            onChange={(e) => setFieldValue("livechatDefaultQueueId", e.target.value)}
                          >
                            <MenuItem value="">
                              {i18n.t("whatsappModal.form.livechatNoQueue")}
                            </MenuItem>
                            {queues.map((queue) => (
                              <MenuItem key={queue.id} value={queue.id}>
                                {queue.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControlLabel
                          className={classes.switchLabel}
                          control={
                            <Switch
                              color="primary"
                              checked={values.livechatPhoneRequired === true}
                              onChange={(e) => setFieldValue("livechatPhoneRequired", e.target.checked)}
                            />
                          }
                          label={i18n.t("whatsappModal.form.livechatPhoneRequired")}
                        />
                      </Grid>
                    </Grid>
                    <Field
                      as={TextField}
                      label={i18n.t("whatsappModal.form.livechatAllowedDomains")}
                      multiline
                      rows={3}
                      fullWidth
                      name="livechatAllowedDomains"
                      placeholder={"www.seusite.com.br\n*.seusite.com.br"}
                      error={touched.livechatAllowedDomains && Boolean(errors.livechatAllowedDomains)}
                      helperText={
                        (touched.livechatAllowedDomains && errors.livechatAllowedDomains) ||
                        i18n.t("whatsappModal.form.livechatAllowedDomainsHelp")
                      }
                      variant="outlined"
                      margin="dense"
                      className={classes.messageField}
                    />
                    <div className={classes.autoCloseHeader}>
                      <Typography className={classes.autoCloseHeaderTitle}>
                        {i18n.t("whatsappModal.form.livechatEnabled")}
                      </Typography>
                      <Switch
                        color="primary"
                        checked={values.livechatEnabled !== false}
                        onChange={(e) => setFieldValue("livechatEnabled", e.target.checked)}
                        inputProps={{ "aria-label": i18n.t("whatsappModal.form.livechatEnabled") }}
                      />
                    </div>

                    {/* CÓDIGO DE INSTALAÇÃO — só após criado */}
                    {(whatsApp.livechatWidgetId) && (
                      <>
                        <Typography className={classes.sectionTitle}>
                          {i18n.t("whatsappModal.livechat.installTitle")}
                        </Typography>
                        <div className={classes.infoBox}>
                          <Lock size={14} strokeWidth={2} />
                          {i18n.t("whatsappModal.livechat.installHelp")}
                        </div>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={buildLivechatSnippet(whatsApp.livechatWidgetId)}
                          variant="outlined"
                          margin="dense"
                          className={classes.readOnlyField}
                          InputProps={{
                            readOnly: true,
                            style: { fontFamily: "monospace", fontSize: "0.78rem" },
                            endAdornment: (
                              <Button
                                className={classes.copyButton}
                                size="small"
                                onClick={() =>
                                  handleCopy(
                                    buildLivechatSnippet(whatsApp.livechatWidgetId),
                                    "livechatSnippet"
                                  )
                                }
                              >
                                {copiedField === "livechatSnippet" ? <Check size={14} /> : <Copy size={14} />}
                              </Button>
                            ),
                          }}
                        />
                        <Button
                          size="small"
                          startIcon={<ExternalLink size={14} />}
                          onClick={() =>
                            window.open(
                              `${API_BASE_URL}/livechat/embed/${whatsApp.livechatWidgetId}?preview=1`,
                              "_blank"
                            )
                          }
                          style={{ marginTop: 4, textTransform: "none" }}
                        >
                          {i18n.t("whatsappModal.livechat.preview")}
                        </Button>
                      </>
                    )}
                  </>
                )}

                {/* QUEUE SELECT - SHARED (livechat usa "Fila padrão" própria) */}
                {values.provider !== "livechat" && (
                  <>
                <Typography className={classes.sectionTitle}>
                  Filas de atendimento
                </Typography>
                <div className={classes.queueChipsContainer}>
                  <Chip
                    label={selectedQueueIds.length === queues.length ? "Desmarcar todas" : "Selecionar todas"}
                    size="small"
                    className={classes.selectAllChip}
                    onClick={handleSelectAllQueues}
                  />
                  {queues.map((queue) => (
                    <Chip
                      key={queue.id}
                      label={queue.name}
                      size="small"
                      className={classes.queueChip}
                      style={{
                        backgroundColor: selectedQueueIds.includes(queue.id)
                          ? queue.color
                          : theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
                        color: selectedQueueIds.includes(queue.id)
                          ? '#FFFFFF'
                          : theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#94a3b8',
                        border: selectedQueueIds.includes(queue.id)
                          ? 'none'
                          : theme.palette.type === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
                      }}
                      onClick={() => handleToggleQueue(queue.id)}
                    />
                  ))}
                </div>

                {/* MESSAGES - SHARED */}
                <Typography className={classes.sectionTitle}>
                  Mensagens automáticas
                </Typography>
                <Field
                  as={TextField}
                  label={i18n.t("whatsappModal.form.greetingMessage") || "Mensagem de saudação"}
                  multiline
                  rows={3}
                  fullWidth
                  name="greetingMessage"
                  error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                  helperText={touched.greetingMessage && errors.greetingMessage}
                  variant="outlined"
                  margin="dense"
                  className={classes.messageField}
                />
                <Field
                  as={TextField}
                  label={i18n.t("whatsappModal.form.complationMessage") || "Mensagem de conclusão"}
                  multiline
                  rows={3}
                  fullWidth
                  name="complationMessage"
                  error={touched.complationMessage && Boolean(errors.complationMessage)}
                  helperText={touched.complationMessage && errors.complationMessage}
                  variant="outlined"
                  margin="dense"
                  className={classes.messageField}
                />
                <Field
                  as={TextField}
                  label={i18n.t("whatsappModal.form.outOfHoursMessage") || "Mensagem fora do expediente"}
                  multiline
                  rows={3}
                  fullWidth
                  name="outOfHoursMessage"
                  error={touched.outOfHoursMessage && Boolean(errors.outOfHoursMessage)}
                  helperText={touched.outOfHoursMessage && errors.outOfHoursMessage}
                  variant="outlined"
                  margin="dense"
                  className={classes.messageField}
                />

                {/* Saudação enviada quando o atendente aceita um ticket
                    pending → open. Toggle e texto vivem na própria conexão
                    (greetingAcceptedEnabled + greetingAcceptedMessage).
                    Em branco com toggle ligado, backend usa template default. */}
                <div className={classes.autoCloseHeader}>
                  <Typography className={classes.autoCloseHeaderTitle}>
                    {i18n.t("whatsappModal.form.greetingAcceptedTitle")}
                  </Typography>
                  <Switch
                    color="primary"
                    checked={Boolean(values.greetingAcceptedEnabled)}
                    onChange={(e) =>
                      setFieldValue("greetingAcceptedEnabled", e.target.checked)
                    }
                    inputProps={{ "aria-label": i18n.t("whatsappModal.form.greetingAcceptedTitle") }}
                  />
                </div>

                {values.greetingAcceptedEnabled && (
                  <>
                    <Field
                      as={TextField}
                      label={i18n.t("whatsappModal.form.greetingAcceptedMessage")}
                      multiline
                      rows={3}
                      fullWidth
                      name="greetingAcceptedMessage"
                      placeholder={i18n.t("whatsappModal.form.greetingAcceptedMessagePlaceholder")}
                      error={touched.greetingAcceptedMessage && Boolean(errors.greetingAcceptedMessage)}
                      helperText={
                        (touched.greetingAcceptedMessage && errors.greetingAcceptedMessage) ||
                        i18n.t("whatsappModal.form.greetingAcceptedMessageHelp")
                      }
                      variant="outlined"
                      margin="dense"
                      className={classes.messageField}
                      inputRef={greetingAcceptedRef}
                    />
                    <MessageVariablesPicker onClick={pickGreetingAcceptedVar} />
                  </>
                )}

                {/* MENSAGENS DE TRANSFERÊNCIA — 3 toggles independentes por
                    variante (só fila / só atendente / fila e atendente).
                    Templates hard-coded em pt/en/es no UpdateTicketService. */}
                <div className={classes.autoCloseHeader}>
                  <Typography className={classes.autoCloseHeaderTitle}>
                    {i18n.t("whatsappModal.form.sendTransferQueueMessageTitle")}
                  </Typography>
                  <Switch
                    color="primary"
                    checked={Boolean(values.sendTransferQueueMessageEnabled)}
                    onChange={(e) =>
                      setFieldValue("sendTransferQueueMessageEnabled", e.target.checked)
                    }
                    inputProps={{ "aria-label": i18n.t("whatsappModal.form.sendTransferQueueMessageTitle") }}
                  />
                </div>

                <div className={classes.autoCloseHeader}>
                  <Typography className={classes.autoCloseHeaderTitle}>
                    {i18n.t("whatsappModal.form.sendTransferUserMessageTitle")}
                  </Typography>
                  <Switch
                    color="primary"
                    checked={Boolean(values.sendTransferUserMessageEnabled)}
                    onChange={(e) =>
                      setFieldValue("sendTransferUserMessageEnabled", e.target.checked)
                    }
                    inputProps={{ "aria-label": i18n.t("whatsappModal.form.sendTransferUserMessageTitle") }}
                  />
                </div>

                <div className={classes.autoCloseHeader}>
                  <Typography className={classes.autoCloseHeaderTitle}>
                    {i18n.t("whatsappModal.form.sendTransferQueueAndUserMessageTitle")}
                  </Typography>
                  <Switch
                    color="primary"
                    checked={Boolean(values.sendTransferQueueAndUserMessageEnabled)}
                    onChange={(e) =>
                      setFieldValue("sendTransferQueueAndUserMessageEnabled", e.target.checked)
                    }
                    inputProps={{ "aria-label": i18n.t("whatsappModal.form.sendTransferQueueAndUserMessageTitle") }}
                  />
                </div>

                {/* ENCERRAMENTO AUTOMÁTICO — expiresTicket=0 significa feature
                    desligada. Ao religar, restauramos o último valor não-zero
                    via lastExpiresTicketRef pra não perder o que estava salvo. */}
                <div className={classes.autoCloseHeader}>
                  <Typography className={classes.autoCloseHeaderTitle}>
                    {i18n.t("whatsappModal.form.autoCloseTitle")}
                  </Typography>
                  <Switch
                    color="primary"
                    checked={Number(values.expiresTicket) > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFieldValue(
                          "expiresTicket",
                          lastExpiresTicketRef.current || 30
                        );
                      } else {
                        const current = Number(values.expiresTicket);
                        if (current > 0) lastExpiresTicketRef.current = current;
                        setFieldValue("expiresTicket", 0);
                      }
                    }}
                    inputProps={{ "aria-label": i18n.t("whatsappModal.form.autoCloseTitle") }}
                  />
                </div>

                {Number(values.expiresTicket) > 0 && (
                  <>
                    <div className={classes.infoBox}>
                      <Clock size={14} strokeWidth={2} />
                      {i18n.t("whatsappModal.form.autoCloseHelp")}
                    </div>
                    <div className={classes.expiresRow}>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.expiresTicket")}
                        name="expiresTicket"
                        variant="outlined"
                        margin="dense"
                        type="number"
                        inputProps={{ min: 1, max: 43200, step: 1 }}
                        error={touched.expiresTicket && Boolean(errors.expiresTicket)}
                        helperText={touched.expiresTicket && errors.expiresTicket}
                        className={classes.textField}
                        style={{ maxWidth: 220 }}
                      />
                      <span className={classes.expiresUnit}>
                        {i18n.t("whatsappModal.form.expiresTicketUnit")}
                      </span>
                    </div>
                    <Field
                      as={TextField}
                      label={i18n.t("whatsappModal.form.expiresInactiveMessage")}
                      multiline
                      rows={3}
                      fullWidth
                      name="expiresInactiveMessage"
                      error={touched.expiresInactiveMessage && Boolean(errors.expiresInactiveMessage)}
                      helperText={touched.expiresInactiveMessage && errors.expiresInactiveMessage}
                      variant="outlined"
                      margin="dense"
                      className={classes.messageField}
                      inputRef={expiresMessageRef}
                    />
                    <MessageVariablesPicker onClick={pickExpiresInactiveVar} />
                  </>
                )}

                {whatsAppId && (
                  <div style={{ marginTop: 24, paddingTop: 16, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: "0.95rem", fontWeight: 600 }}>
                          {i18n.t("whatsappModal.schedules.title")}
                        </div>
                        <div style={{ fontSize: "0.8rem", opacity: 0.65 }}>
                          {i18n.t("whatsappModal.schedules.description")}
                        </div>
                      </div>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={schedulesOverride}
                            onChange={handleToggleSchedulesOverride}
                            color="primary"
                            disabled={schedulesSaving}
                          />
                        }
                        label={i18n.t("whatsappModal.schedules.custom")}
                        labelPlacement="start"
                      />
                    </div>

                    {schedulesOverride && (
                      <SchedulesForm
                        loading={schedulesSaving}
                        onSubmit={handleSaveSchedules}
                        initialValues={whatsappSchedules || []}
                        labelSaveButton={i18n.t("whatsappModal.schedules.save")}
                      />
                    )}
                  </div>
                )}
                  </>
                )}
              </DialogContent>

              <DialogActions className={classes.dialogActions}>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  variant="outlined"
                  className={classes.cancelBtn}
                  startIcon={<X size={16} strokeWidth={1.75} />}
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="contained"
                  color="primary"
                  className={`${classes.btnWrapper} ${classes.submitBtn}`}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={14} style={{ color: "#fff" }} />
                    ) : whatsAppId ? (
                      <Pencil size={16} strokeWidth={1.75} />
                    ) : (
                      <Plus size={16} strokeWidth={1.75} />
                    )
                  }
                >
                  {whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                </Button>
              </DialogActions>
            </Form>
            );
          }}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
